import { useApi } from './useApi.ts'
import { Map } from '@/js/types'
import { useAtomValue, useSetAtom } from 'jotai'
import { loadMapAtom } from '@/js/editor/state/mapSessionAtoms.ts'
import { mapSessionAtom, mapSessionIdAtom } from '@/js/editor/state/mapAtoms.ts'

type MapUpdateData = {
    name: string
    zoom: number
    center: [number, number]
    uses_tags_as_categories?: boolean | null
    show_short_description_in_sidebar?: boolean | null
    color?: string
    highlight_color?: string
    primary_color?: string
    colors?: {
        standard: string
        highlight: string
        primary: string
        background: string
    }
}
export const useMaps = () => {
    const mapId = useAtomValue(mapSessionIdAtom)
    const loadMap = useSetAtom(loadMapAtom)
    const setMapSession = useSetAtom(mapSessionAtom)

    const { callEndpoint } = useApi()

    const load = async () => {
        if (!mapId) {
            return
        }

        return loadMap({
            mapId,
            request: (requestedMapId) => callEndpoint(`/api/maps/${requestedMapId}`),
        })
    }

    const update = async (mapData: MapUpdateData) => {
        if (!mapId) {
            throw new Error('Cannot update a map without a map ID')
        }

        const requestData = {
            name: mapData.name,
            zoom: mapData.zoom,
            center: mapData.center,
            uses_tags_as_categories: mapData.uses_tags_as_categories,
            show_short_description_in_sidebar: mapData.show_short_description_in_sidebar,
            primary_color: mapData.colors.primary,
            color: mapData.colors.standard,
            highlight_color: mapData.colors.highlight,
            background_color: mapData.colors.background,
        }

        const response = await callEndpoint(`/api/maps/${mapId}`, {
            method: 'PUT',
            body: JSON.stringify(requestData),
        })

        if (!response.ok) {
            throw new Error(`Unable to update map (${response.status})`)
        }

        const updatedMap = (await response.json()) as Map

        setMapSession((session) => {
            if (!session.map) {
                return {
                    ...session,
                    id: updatedMap.id,
                    map: updatedMap,
                }
            }

            return {
                ...session,
                id: updatedMap.id,
                map: {
                    ...session.map,
                    ...updatedMap,
                    pois: updatedMap.pois ?? session.map.pois,
                    tags: updatedMap.tags ?? session.map.tags,
                },
            }
        })

        return updatedMap
    }

    return {
        update,
        load,
    }
}
